import React from 'react';
import { datadogRum } from '@datadog/browser-rum-slim';

interface ErrorState {
  hasError: boolean;
}

interface ErrorProps {
  children?: React.ReactNode;
  errorComponent?: React.ReactNode;
}

/**
 * Notes on ErrorBoundaries:
 * This must be a class comopnent to allow access to the componentDidCatch
 * lifecycle method. Currently no hooks-based equivalent exists as of React v17
 *
 * Error boundaries don't work with async operations, SSR, or event handlers
 *
 * https://reactjs.org/docs/error-boundaries.html
 */
class ErrorBoundary extends React.Component<ErrorProps, ErrorState> {
  state: ErrorState = { hasError: false };

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    datadogRum.addError(error, {
      reactErrorInfo: errorInfo,
      pathname: window?.location?.pathname,
    });

    // eslint-disable-next-line no-console
    console.error(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      /**
       * We don't have a default custom error state for our UIs, so we add the option
       * to pass a ReactNode as a prop, or render an empty fragment as a fallback
       */
      return this.props.errorComponent || null;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
